import { motion } from 'framer-motion';
import { CSSProperties } from 'react';

import { Gender } from '@/enums/apiEnums.ts';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import ProfileMaleShape from '@/components/common/svg/shapes/ProfleMaleShape.tsx';
import ProfileFemaleShape from '@/components/common/svg/shapes/ProfleFemaleShape.tsx';

interface AvatarWithProgressBarProps {
  profilePhoto?: string | null;
  fallback?: string;
  thumbhash?: string;
  gender?: Gender;
  profileCompletion: number;
  avatarSize?: number;
  progressCircleRadius?: number;
  strokeColor?: string;
  strokeWidth?: number;
  percentageStyle?: CSSProperties;
  containerStyle?: CSSProperties;
}

export const AvatarWithProgressBar: React.FC<AvatarWithProgressBarProps> = ({
  profilePhoto,
  fallback,
  thumbhash,
  gender,
  profileCompletion,
  avatarSize = 101,
  progressCircleRadius = 55,
  strokeColor = '#851989',
  strokeWidth = 9,
  percentageStyle,
  containerStyle
}) => {
  const circumference = 2 * Math.PI * progressCircleRadius;
  const maxProgress = 0.9;
  const progressLength =
    (profileCompletion / 100) * circumference * maxProgress;

  const profilePlaceholder =
    gender === Gender.MALE ? (
      <ProfileMaleShape height={avatarSize} width={avatarSize} />
    ) : (
      <ProfileFemaleShape height={avatarSize} width={avatarSize} />
    );

  return (
    <div
      style={{
        position: 'relative',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0px 0px 1px 0px #00000040',
        ...containerStyle
      }}
    >
      {profilePhoto ? (
        <ImageWithLoader
          fullWidth={false}
          src={profilePhoto}
          fallback={fallback}
          thumbhash={thumbhash}
          alt=""
          style={{
            position: 'relative',
            width: `${avatarSize}px`,
            height: `${avatarSize}px`,
            borderRadius: '50%',
            objectFit: 'cover',
            background: '#ffffff',
            zIndex: 3
          }}
        />
      ) : (
        <div
          style={{
            position: 'relative',
            zIndex: 3,
            width: `${avatarSize}px`,
            height: `${avatarSize}px`,
            borderRadius: '50%',
            overflow: 'hidden' // Ensures the content is clipped to the container
          }}
        >
          <svg
            width={avatarSize}
            height={avatarSize}
            style={{ display: 'block' }}
          >
            <defs>
              <clipPath id="circleClip">
                <circle
                  cx={avatarSize / 2}
                  cy={avatarSize / 2}
                  r={avatarSize / 2}
                />
              </clipPath>
            </defs>
            <g clipPath="url(#circleClip)">{profilePlaceholder}</g>
          </svg>
        </div>
      )}
      <svg
        width={`${avatarSize + 32}`}
        height={`${avatarSize + 32}`}
        style={{
          position: 'absolute',
          zIndex: 2,
          transform: 'rotate(100deg)'
        }}
      >
        <circle
          cx={(avatarSize + 32) / 2}
          cy={(avatarSize + 32) / 2}
          r={progressCircleRadius}
          fill="none"
          stroke="#F4F4F4"
          strokeWidth={strokeWidth}
        />

        <circle
          cx={(avatarSize + 32) / 2}
          cy={(avatarSize + 32) / 2}
          r={progressCircleRadius}
          fill="none"
          stroke="#fff"
          strokeWidth={strokeWidth}
        />

        <motion.circle
          cx={(avatarSize + 32) / 2}
          cy={(avatarSize + 32) / 2}
          r={progressCircleRadius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progressLength}, ${circumference}`}
          strokeDashoffset="0"
          initial={{ strokeDasharray: `0, ${circumference}` }}
          animate={{ strokeDasharray: `${progressLength}, ${circumference}` }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          bottom: '-17px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: strokeColor,
          color: '#fff',
          width: 53,
          height: 28,
          borderRadius: '20px',
          fontSize: '14px',
          fontWeight: 'bold',
          border: '3px solid #fff',
          zIndex: 5,
          display: 'flex', // Add flexbox to center content
          justifyContent: 'center', // Center horizontally
          alignItems: 'center', // Center vertically
          ...percentageStyle
        }}
      >
        {profileCompletion}%
      </div>
    </div>
  );
};